import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { ChevronDown } from "lucide-react";
import { useSeo, breadcrumb, SITE_URL } from "../lib/seo";

const SORTS = [
  { value: "newest", label: "Sélection" },
  { value: "price_asc", label: "Prix croissant" },
  { value: "price_desc", label: "Prix décroissant" },
];

export default function Shop() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [sort, setSort] = useState(searchParams.get("sort") || "newest");
  const [open, setOpen] = useState({}); // {slug: true}
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/categories").then(r => setCats(r.data)); }, []);

  // Auto-open the parent of the currently-viewed sub-category
  useEffect(() => {
    if (!slug || !cats.length) return;
    const current = cats.find(c => c.slug === slug);
    if (current?.parent_slug) {
      setOpen(o => ({ ...o, [current.parent_slug]: true }));
    } else if (current && cats.some(c => c.parent_slug === current.slug)) {
      setOpen(o => ({ ...o, [current.slug]: true }));
    }
  }, [slug, cats]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (slug) {
      if (slug === "nouveautes") params.set("is_new", "true");
      else if (slug === "soldes") params.set("on_sale", "true");
      else params.set("category", slug);
    }
    if (sort) params.set("sort", sort);
    setLoading(true);
    api.get(`/products?${params.toString()}`)
      .then(r => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [slug, sort]);

  const toggle = (s) => setOpen(o => ({ ...o, [s]: !o[s] }));

  const current = cats.find(c => c.slug === slug);
  const title = slug ? (current?.name || (slug==="nouveautes" ? "Nouveautés" : slug==="soldes" ? "Soldes" : slug)) : "La boutique";
  const intro = current?.description?.trim();

  // Per-page SEO: own canonical, CollectionPage + ItemList + breadcrumb.
  const path = slug ? `/categorie/${slug}` : "/boutique";
  const parent = current?.parent_slug ? cats.find(c => c.slug === current.parent_slug) : null;
  const trail = [{ name: "Accueil", path: "/" }, { name: "Boutique", path: "/boutique" }];
  if (parent) trail.push({ name: (parent.name || "").trim(), path: `/categorie/${parent.slug}` });
  if (slug) trail.push({ name: (title || "").trim(), path });
  useSeo({
    title: slug ? (title || "").trim() : "La boutique",
    description: intro
      || (slug
        ? `${(title || "").trim()} — la sélection BERULIA de prêt-à-porter féminin. Livraison offerte dès 79 €.`
        : "Tout le prêt-à-porter féminin BERULIA : robes, tops, jupes, pantalons, accessoires. Livraison offerte dès 79 €."),
    path,
    jsonLd: loading ? null : {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "CollectionPage",
          "@id": `${SITE_URL}${path}#webpage`,
          url: `${SITE_URL}${path}`,
          name: `${(title || "").trim()} — BERULIA`,
          ...(intro ? { description: intro } : {}),
          inLanguage: "fr-FR",
          isPartOf: { "@id": `${SITE_URL}/#website` },
        },
        {
          "@type": "ItemList",
          numberOfItems: products.length,
          itemListElement: products.slice(0, 60).map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${SITE_URL}/produit/${p.id}`,
            name: p.name,
          })),
        },
        breadcrumb(trail),
      ],
    },
  });

  return (
    <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-12">
      {/* Page heading */}
      <div className="border-b border-berulia-border pb-8 mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline text-berulia-inkmuted">BERULIA · Saison 2026</p>
          <h1 className="font-heading text-5xl md:text-6xl mt-2 capitalize tracking-tight">{title}</h1>
          {/* The API has carried a description per category all along; it was
              never rendered. Free editorial content on the category pages. */}
          {intro && <p className="mt-3 max-w-2xl text-berulia-inkmuted">{intro}</p>}
        </div>
        <p className="text-sm text-berulia-inkmuted">{products.length} pièce{products.length > 1 ? "s" : ""}</p>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-10">
        {/* Sidebar — categories only (accordion) */}
        <aside className="lg:sticky lg:top-32 self-start" data-testid="filter-sidebar">
          <p className="overline mb-4">Catégories</p>
          <ul className="space-y-1.5 text-sm">
            <li>
              <a href="/boutique" className={`block py-1 hover:text-berulia-ink ${!slug ? "text-berulia-ink font-medium" : "text-berulia-inkmuted"}`}>— Tout voir</a>
            </li>
            {cats.filter(c => !c.parent_slug && c.slug !== "soldes").sort((a,b)=>a.sort_order-b.sort_order).map(parent => {
              const kids = cats.filter(c => c.parent_slug === parent.slug).sort((a,b)=>a.sort_order-b.sort_order);
              const hasKids = kids.length > 0;
              const isOpen = !!open[parent.slug];
              return (
                <li key={parent.id}>
                  {hasKids ? (
                    <button
                      onClick={() => toggle(parent.slug)}
                      data-testid={`toggle-${parent.slug}`}
                      className={`flex items-center gap-2 py-1.5 hover:text-berulia-ink ${slug===parent.slug ? "text-berulia-ink font-medium" : "text-berulia-inkdeep"}`}>
                      <span>— {parent.name}</span>
                      <ChevronDown size={14} className={`transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}/>
                    </button>
                  ) : (
                    <a href={`/categorie/${parent.slug}`} data-testid={`filter-cat-${parent.slug}`}
                      className={`block py-1.5 hover:text-berulia-ink ${slug===parent.slug ? "text-berulia-ink font-medium" : "text-berulia-inkmuted"}`}>
                      — {parent.name}
                    </a>
                  )}
                  {hasKids && (
                    <div className={`grid transition-all duration-300 ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                      <ul className="overflow-hidden ml-3 border-l border-berulia-rose/40 pl-4">
                        <li><a href={`/categorie/${parent.slug}`} data-testid={`filter-cat-${parent.slug}`}
                          className={`block py-1 text-[13px] hover:text-berulia-ink ${slug===parent.slug ? "text-berulia-ink font-medium" : "text-berulia-inkmuted italic font-serif-italic"}`}>
                          Tout {parent.name.toLowerCase()}
                        </a></li>
                        {kids.map(k => (
                          <li key={k.id}>
                            <a href={`/categorie/${k.slug}`} data-testid={`filter-cat-${k.slug}`}
                              className={`block py-1 text-[13px] hover:text-berulia-ink ${slug===k.slug ? "text-berulia-ink font-medium" : "text-berulia-inkmuted"}`}>
                              {k.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Main */}
        <div>
          <div className="flex items-center justify-end border-b border-berulia-border pb-4 mb-8">
            <div className="relative">
              <select value={sort} onChange={e => setSort(e.target.value)} data-testid="sort-select"
                className="appearance-none bg-transparent border-b border-berulia-ink overline pr-7 py-1 outline-none cursor-pointer">
                {SORTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <ChevronDown size={14} className="absolute right-0 top-1.5 pointer-events-none"/>
            </div>
          </div>

          {/* Skeleton while the API answers — the empty state used to show
              "Aucune pièce ne correspond" on every load, i.e. an empty shop. */}
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12" data-testid="shop-skeleton" aria-busy="true">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-berulia-beigewarm"/>
                  <div className="h-3 w-2/3 bg-berulia-beigewarm mt-4"/>
                  <div className="h-3 w-1/4 bg-berulia-beigewarm mt-2"/>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <p className="text-center text-berulia-inkmuted py-20 font-serif-italic text-xl">Aucune pièce ne correspond à votre sélection.</p>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
              {products.map((p,i) => <ProductCard key={p.id} product={p} index={i}/>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
